import {
  NOTIFICATION_ENTITY_TYPE,
  PLATFORM_NOTIFICATION_ACTION_TYPE,
  PLATFORM_NOTIFICATION_HEALTH_TYPE,
  type ApplicationNotificationDto,
  type MarkEntityViewedBodyDto,
  type NotificationEntityType,
  type PlatformNotificationActionType,
  type PlatformNotificationHealthType,
} from '@terra/shared-contracts-notification-center';
import type { ApplicationHealthIssue } from '@terra/ui-design-system';
import type { ApplicationSettingsTabId } from '../../store/application-settings-tab-id';
import { getFindingTabInput } from '../tabs/assets-table/asset-tab-inputs';
import { sidebarTabMapping } from '../layout/utils';
import type { HomeDestination } from './home-destination';

const HEALTH_ISSUE_SETTINGS_SECTION: Record<
  PlatformNotificationHealthType,
  ApplicationSettingsTabId
> = {
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.CREDENTIAL_VERIFICATION_FAILED]:
    'credentials',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.WAF_BLOCKING_APPLICATION_URL]:
    'networking',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.WAF_BLOCKING_ADDITIONAL_DOMAIN]:
    'networking',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.INTERNAL_CONNECTOR_OFFLINE]: 'networking',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.INTERNAL_CONNECTOR_REQUIRED]: 'networking',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_INTEGRATION_UNREACHABLE]:
    'source-code',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_REPOSITORIES_UNREACHABLE]:
    'source-code',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_CONNECTOR_OFFLINE]:
    'source-code',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.MISSING_API_SPECIFICATION]: 'documents',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.NO_SIGNAL_CATEGORIES_IN_SCOPE]:
    'signal-categories',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.KNOWLEDGE_SOURCE_SYNC_FAILED]:
    'knowledge-sources',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.CLOUD_IP_UNASSOCIATED]: 'general',
  [PLATFORM_NOTIFICATION_HEALTH_TYPE.ADAPTIVE_SCANNING_PAUSED]: 'automation',
};

const FINDING_ACTION_TYPES: ReadonlySet<PlatformNotificationActionType> =
  new Set([
    PLATFORM_NOTIFICATION_ACTION_TYPE.TRIAGE_NEW,
    PLATFORM_NOTIFICATION_ACTION_TYPE.CONTINUE_REMEDIATION,
    PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_RETEST_STILL_VULNERABLE,
    PLATFORM_NOTIFICATION_ACTION_TYPE.INVESTIGATE_RETEST_FAILURE,
  ]);

const VIEWED_ON_OPEN_ENTITY_TYPE: Partial<
  Record<PlatformNotificationActionType, NotificationEntityType>
> = {
  [PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_NEW_MEMORY]:
    NOTIFICATION_ENTITY_TYPE.MEMORY,
  [PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_RETEST_STILL_VULNERABLE]:
    NOTIFICATION_ENTITY_TYPE.FINDING,
};

const isActionType = (type: string): type is PlatformNotificationActionType =>
  Object.values<string>(PLATFORM_NOTIFICATION_ACTION_TYPE).includes(type);

const isHealthType = (type: string): type is PlatformNotificationHealthType =>
  Object.values<string>(PLATFORM_NOTIFICATION_HEALTH_TYPE).includes(type);

const readContextDate = (
  context: ApplicationNotificationDto['context'],
  key: string,
): Date | null => {
  const value = context?.[key];

  if (typeof value !== 'string') {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const readContextLabel = (
  context: ApplicationNotificationDto['context'],
): string => {
  const label = context?.entityLabel ?? context?.title;

  return typeof label === 'string' ? label : '';
};

export const resolveActionDestination = (
  action: ApplicationNotificationDto,
): HomeDestination | null => {
  if (!isActionType(action.type)) {
    return null;
  }

  if (FINDING_ACTION_TYPES.has(action.type)) {
    return action.entityType === NOTIFICATION_ENTITY_TYPE.FINDING &&
      action.entityId
      ? {
          kind: 'tab',
          tab: getFindingTabInput({
            id: action.entityId,
            title: readContextLabel(action.context) || action.entityId,
          }),
        }
      : null;
  }

  switch (action.type) {
    case PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_NEW_MEMORY:
      return { kind: 'settings', section: 'memories' };
    case PLATFORM_NOTIFICATION_ACTION_TYPE.NEW_SCHEDULED_REPORT:
      return { kind: 'tab', tab: sidebarTabMapping.reports };
    case PLATFORM_NOTIFICATION_ACTION_TYPE.BUG_BOUNTY_SIGNAL_IMPORT_SUMMARY:
    case PLATFORM_NOTIFICATION_ACTION_TYPE.SIGNAL_IMPORT_COMPLETED:
      return {
        kind: 'list',
        destination: {
          list: 'signals',
          filters: {
            createdStartDate: readContextDate(action.context, 'startedAt'),
          },
        },
      };
    default:
      return null;
  }
};

export const toHealthIssues = (
  notifications: ApplicationNotificationDto[],
): ApplicationHealthIssue[] =>
  notifications.flatMap((notification) =>
    isHealthType(notification.type)
      ? [
          {
            id: notification.id,
            type: notification.type,
            createdAt: new Date(notification.createdAt),
            context: notification.context,
          },
        ]
      : [],
  );

export const resolveHealthIssueDestination = (
  type: string,
): HomeDestination | null =>
  isHealthType(type)
    ? { kind: 'settings', section: HEALTH_ISSUE_SETTINGS_SECTION[type] }
    : null;

export const resolveViewedEntityOnOpen = (
  action: ApplicationNotificationDto,
): MarkEntityViewedBodyDto | null => {
  if (!isActionType(action.type) || !action.entityId) {
    return null;
  }

  const entityType = VIEWED_ON_OPEN_ENTITY_TYPE[action.type];

  return entityType && entityType === action.entityType
    ? { entityType, entityId: action.entityId }
    : null;
};
